import { Divider, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, PlotReportFactLabelNameEnum, R1FactType } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GraphFact } from '@/components/Charts/types';
import {
  NCCard,
  NCCard2SectionLayout,
  NCCardAnalysisTypeToggle,
  NCCardComplianceFooter,
  NCCardHeader,
  useNCCardContext,
} from '@/components/NCCard/NCCard';
import { WaterExplainer } from '@/components/NCExplainers/WaterExplainer';
import { COMPLIANCE_LOGOS_WATER } from '@/config/constants';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { PlotCardWrapper } from '../PlotCardWrapper';
import { WaterHistoricalGraphTile } from './HistoricalGraphTile';
import { WaterHistoricalStatsTile, WaterHoldingCapacityExplantionBanner } from './HistoricalStatsTile';

export const WaterHoldingCapacity = () => {
  const { t } = useTranslation();

  const [showCard, setShowCard] = useState(true);

  if (!showCard) {
    return null;
  }

  return (
    <PlotCardWrapper name={t('global.analysis.water')}>
      <NCCard explainer={<WaterExplainer />} data-testid='water-nca'>
        <WaterHoldingCapacityPlotCard setShowCard={setShowCard} />
      </NCCard>
    </PlotCardWrapper>
  );
};

const WaterHoldingCapacityPlotCard = ({ setShowCard }: { setShowCard: (show: boolean) => void }) => {
  const { t } = useTranslation();
  const isSmallScreen = useScreenSize() === 'small';
  const { analysisType } = useNCCardContext();

  const factNames = {
    total: {
      graph: R1FactType.r1_historic_water_and_soil_moisture_graph,
      benchmark: R1FactType.water_fc_total_benchmark,
      latestAnalysis: R1FactType.r1_water_holding_capacity_total,
      oneYearUplift: R1FactType.r1_whc_previous_1y_uplift_total,
      oneYearPctChange: R1FactType.r1_water_whc_increase_previous_1y_uplift_total_pct_change,
      soilMoistureLatestAnalysis: R1FactType.r1_soil_moisture_latest_analysis_total,
      soilMoistureTrendChange: R1FactType.r1_soil_moisture_trend_change,
      precipitationTrendChange: R1FactType.r1_precipitation_trend_change,
    },
    perHa: {
      graph: R1FactType.r1_historic_water_and_soil_moisture_per_ha_graph,
      benchmark: R1FactType.water_fc_per_ha_benchmark,
      latestAnalysis: R1FactType.r1_water_holding_capacity_per_ha,
      oneYearUplift: R1FactType.r1_whc_previous_1y_uplift_per_ha,
      oneYearPctChange: R1FactType.r1_water_whc_increase_previous_1y_uplift_per_ha_pct_change,
      soilMoistureLatestAnalysis: R1FactType.r1_soil_moisture_latest_analysis_per_ha,
      soilMoistureTrendChange: R1FactType.r1_soil_moisture_trend_change,
      precipitationTrendChange: R1FactType.r1_precipitation_trend_change,
    },
  };

  const factSet = analysisType === 'total' ? factNames.total : factNames.perHa;

  const facts = usePlotFacts(Object.values(factSet));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factSet.graph)?.value;

  const benchmarkFact = {
    high: getFactFromFactArray<number>(facts, factSet.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    }),
    moderate: getFactFromFactArray<number>(facts, factSet.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    }),
    low: getFactFromFactArray<number>(facts, factSet.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
    }),
  };

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.historic_water_soil_moisture;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);

  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);

  const plot = usePlot().data;
  const plotArea = plot.area;

  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal, plotArea);

  const latestAnalysisFact = getFactFromFactArray<number>(facts, factSet.latestAnalysis);
  const oneYearUpliftFact = getFactFromFactArray<number>(facts, factSet.oneYearUplift);
  const oneYearPctChangeFact = getFactFromFactArray<number>(facts, factSet.oneYearPctChange);
  const soilMoistureLatestAnalysisFact = getFactFromFactArray<number>(facts, factSet.soilMoistureLatestAnalysis);
  const soilMoistureTrendChangeFact = getFactFromFactArray<number>(facts, factSet.soilMoistureTrendChange);
  const precipitationTrendChangeFact = getFactFromFactArray<number>(facts, factSet.precipitationTrendChange);

  const hasChartData = !isNullish(graphData);
  const hasStatsData =
    !isNullish(latestAnalysisFact?.value) ||
    !isNullish(oneYearUpliftFact?.value) ||
    !isNullish(oneYearPctChangeFact?.value) ||
    !isNullish(soilMoistureLatestAnalysisFact?.value) ||
    !isNullish(soilMoistureTrendChangeFact?.value) ||
    !isNullish(precipitationTrendChangeFact?.value);

  useEffect(() => {
    if (!hasChartData && !hasStatsData) {
      setShowCard(false);
    }
  }, [hasChartData, hasStatsData, setShowCard]);

  return (
    <>
      <NCCardHeader title={t('global.analysis.water')}>
        {!isSmallScreen && <NCCardAnalysisTypeToggle data-testid='water-toggle' />}
      </NCCardHeader>
      <NCCard2SectionLayout>
        <Stack className='items-center pt-4 sm:items-stretch sm:pt-0'>
          {isSmallScreen && <NCCardAnalysisTypeToggle data-testid='water-toggle' />}
          <WaterHistoricalGraphTile
            graphData={graphData}
            benchmarkFact={benchmarkFact}
            activeCustomBenchmark={displayCustomBenchmark}
            selectedBenchmarkId={selectedBenchmarkId}
            onBenchmarkSelect={setSelectedBenchmarkId}
          />
        </Stack>
        <WaterHistoricalStatsTile
          latestAnalysisFact={latestAnalysisFact}
          oneYearUpliftFact={oneYearUpliftFact}
          oneYearPctChangeFact={oneYearPctChangeFact}
          soilMoistureLatestAnalysisFact={soilMoistureLatestAnalysisFact}
          soilMoistureTrendChangeFact={soilMoistureTrendChangeFact}
          precipitationTrendChangeFact={precipitationTrendChangeFact}
        />
      </NCCard2SectionLayout>
      <WaterHoldingCapacityExplantionBanner
        oneYearPctChangeFact={oneYearPctChangeFact}
        benchmarkFact={benchmarkFact}
        latestAnalysisFact={latestAnalysisFact}
      />
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_WATER} />
    </>
  );
};
